import { connect } from 'react-redux';
import { css, StyleSheet } from 'aphrodite';
import { accessibilityUtils, fonts, media, mixins } from '@blend/consumer-components';
import * as React from 'react';
import * as _ from 'lodash';

import * as privacyPolicyAPI from 'login-apis/privacyPolicyAPI';
import { getWindow } from 'login-utils/globalWrappers';
import * as deploymentConfig from 'login-utils/deploymentConfig';
import * as footerConfigs from 'login-components/Common/Footer/footerConfigs';
import * as mobileUtils from 'login-utils/mobileUtils';
import States, { isState } from 'login-constants/states';
import EqualHousingLenderSVG from 'login-components/Common/svg/EqualHousingLenderSVG';
import * as userUtils from 'login-utils/userUtils';
import TextScreenModalTrigger from 'login-components/Common/TextScreenModal/TextScreenModalTrigger';
import ContextLink from '../ContextLink';
import RightPane from './RightPane/RightPane';
import { LeftPaneProps } from './LeftPane/LeftPane';
import { getApplicationType } from './paneLayoutUtils';

const loginBackgroundColor = deploymentConfig.getOrganizationColors().loginBackground;
const dynamicColor = mixins.dynamicColor(loginBackgroundColor, 0.7);

const styles = StyleSheet.create({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    [media.mediumView]: {
      flexDirection: 'row',
      height: '100vh',
    },
  },
  contextWrapper: {
    ...fonts.Roboto,
    color: dynamicColor,
    display: 'block',
    fontSize: 13,
    fontWeight: fonts.weight.semibold,
    marginBottom: 20,
  },
  pane: {
    [media.mediumView]: {
      height: '100vh',
      position: 'fixed',
      top: 0,
    },
  },
  leftPane: {
    [media.mediumView]: {
      left: 0,
      width: '40vw',
    },
    [media.largeView]: {
      width: '35vw',
    },
  },
  rightPane: {
    [media.mediumView]: {
      right: 0,
      width: '60vw',
    },
    [media.largeView]: {
      width: '65vw',
    },
  },
  footer: mixins.padding(16, 20, 20),
  footerLinksWrapper: {
    display: 'inline-block',
    paddingRight: 10,
    position: 'relative',
  },
  footerEl: {
    paddingBottom: 4,
  },
  footerText: {
    color: dynamicColor,
    display: 'inline-block',
    paddingRight: 10,
  },
  footerLink: {
    textDecoration: 'underline',
    cursor: 'pointer',
  },
  footerDividerBar: {
    bottom: -1,
    color: dynamicColor,
    fontSize: 16,
    position: 'relative',
    paddingBottom: 7,
    paddingRight: 10,
  },
  footerLinksLogoWrapper: {
    ...fonts.Roboto,
    'align-items': 'center',
    color: dynamicColor,
    fontSize: 11,
    display: 'flex',
  },
  blendLogoWrapper: {
    display: 'inline-block',
  },
  svgWrapper: {
    paddingRight: 8,
  },
});

interface PaneLayoutComponents {
  left: React.ReactElement<LeftPaneProps>;
  right: React.ReactFragment;
  top?: React.ReactFragment;
}

export interface PaneLayoutProps extends PaneLayoutComponents {
  viewType: string;
  loanType: LoanType;
}

function getAccountOpeningFooterHyperlink({ financialInstitutionType }: Organization): string {
  return financialInstitutionType === 'CREDIT_UNION'
    ? 'https://www.ncua.gov/Pages/default.aspx'
    : 'https://www.fdic.gov/';
}

function getAccountOpeningFooterDisplayText({ financialInstitutionType }: Organization): string {
  return financialInstitutionType === 'CREDIT_UNION' ? 'Insured by NCUA' : 'Member FDIC';
}

export class PaneLayout extends React.Component<PaneLayoutProps> {
  renderLicensingInfo() {
    const lenderLicensingInformation = _.get(
      deploymentConfig.get(),
      'loanOfficerPages.lenderLicensingInformation',
    ) as string;
    if (lenderLicensingInformation) {
      return (
        <TextScreenModalTrigger
          textScreenContent={lenderLicensingInformation}
          headerText="Licensing Information"
          renderTrigger={({ openModal }) => (
            <span
              className={css(styles.footerEl, styles.footerLink, styles.footerText)}
              onClick={openModal}
              onKeyDown={openModal && accessibilityUtils.generateOnKeyDown(openModal)}
              tabIndex={0}
            >
              Licensing Information
            </span>
          )}
        />
      );
    }
  }
  displayFastForward(location) {
    return !(isState(location.pathname, States.FAST_FORWARD) || userUtils.getCurrentUser());
  }

  renderTOSAndLicensingInfo() {
    const { loanType } = this.props;
    return (
      <div className={css(styles.footerLinksLogoWrapper)}>
        <TextScreenModalTrigger
          textScreenId={'terms'}
          headerText="Terms Of Use"
          renderTrigger={({ openModal }) => (
            // TODO (UI-5264) : Make trigger button a shared component since they share a lot of functionality
            <span
              className={css(styles.footerEl, styles.footerLink, styles.footerText)}
              onClick={openModal}
              onKeyDown={openModal && accessibilityUtils.generateOnKeyDown(openModal)}
              tabIndex={0}
            >
              Terms of Use
            </span>
          )}
        />
        <TextScreenModalTrigger
          textScreenId={privacyPolicyAPI.getPrivacyPolicyTextScreenId()}
          headerText="Privacy Policy"
          renderTrigger={({ openModal }) => (
            <span
              className={css(styles.footerEl, styles.footerLink, styles.footerText)}
              onClick={openModal}
              onKeyDown={openModal && accessibilityUtils.generateOnKeyDown(openModal)}
              tabIndex={0}
            >
              Privacy Policy
            </span>
          )}
        />
        {loanType === 'ACCOUNT_OPENING' ? this.renderAccountOpeningLinks() : this.renderLicensingInfo()}
      </div>
    );
  }

  renderNMLS() {
    return (
      _.get(deploymentConfig.get(), 'organization.nmlsId') && (
        <React.Fragment>
          <p data-it="OrgNMLS" className={css(styles.footerEl, styles.footerText)}>
            NMLS #{_.get(deploymentConfig.get(), 'organization.nmlsId')}
          </p>
          <span className={css(styles.footerDividerBar)}>|</span>
        </React.Fragment>
      )
    );
  }

  renderEqualHousingLogo() {
    return (
      <React.Fragment>
        <div data-it="EqualHousingLogo" className={css(styles.svgWrapper, styles.footerEl)}>
          <EqualHousingLenderSVG />
        </div>
        <p className={css(styles.footerEl)}>Equal Housing Lender</p>
      </React.Fragment>
    );
  }

  renderAccountOpeningLinks() {
    const { organization } = deploymentConfig.get();
    const href = getAccountOpeningFooterHyperlink(organization);
    const displayText = getAccountOpeningFooterDisplayText(organization);
    return (
      <a
        className={css(styles.footerEl, styles.footerText)}
        data-it="accountOpeningFooterLink"
        href={href}
        target="_blank"
      >
        {displayText}
      </a>
    );
  }

  renderLenderInfo() {
    const { loanType } = this.props;

    if (_.includes(['MORTGAGE', 'HELOC', 'HELOAN'], loanType)) {
      return (
        <div className={css(styles.footerLinksLogoWrapper)}>
          {this.renderNMLS()}
          {this.renderEqualHousingLogo()}
        </div>
      );
    } else {
      return null;
    }
  }

  renderFooter() {
    const config = footerConfigs.linkToFastForward;

    return (
      <div data-it="PaneLayout-footer">
        {getWindow().ALLOW_FAST_FORWARD && this.displayFastForward(location) && (
          <ContextLink to={config.link.state} id={config.link.id} className={css(styles.contextWrapper)}>
            {config.link.text}
          </ContextLink>
        )}
        {this.renderTOSAndLicensingInfo()}
        {this.renderLenderInfo()}
      </div>
    );
  }

  render() {
    const { left, right, top, viewType } = this.props;
    return (
      <div className={css(styles.wrapper)}>
        <div className={css(styles.pane, styles.leftPane)}>
          {React.cloneElement<LeftPaneProps>(left, {
            footer: viewType !== mobileUtils.viewTypes.MOBILE && this.renderFooter(),
          })}
        </div>
        <div className={css(styles.pane, styles.rightPane)}>
          <RightPane top={top}>{right}</RightPane>
        </div>
        {viewType === mobileUtils.viewTypes.MOBILE && <div className={css(styles.footer)}>{this.renderFooter()}</div>}
      </div>
    );
  }
}

export default connect((state: ReduxState) => ({
  viewType: state.global.viewType,
  loanType: getApplicationType(state),
}))(PaneLayout);
